import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { NotifyService } from '@app/services';
import { BadgeType } from '@humi-design-library/components/Badge/types';
import { Employee } from '@models/employee/employee.model';
import { Feedback } from '@models/performance/feedback.model';
import { Goal } from '@models/performance/goal.model';
import { ReviewAssignment } from '@models/performance/review-assignment.model';
import { Report } from '@reporting/services/queries/report';
import { FeedbackTypesDisplayOptions } from '@app/types/translatables/feedback-display.options';

/**
 * Stateful object storing how a question looked when answered by employee
 */
interface QuestionState {
    index: number;
    label: string;
    icon: string;
    type: string;
    question: string;
    choices?: string[];
}

/**
 * Combined state of an answered question. This is built on page load, not sent for API.
 */
interface AnsweredQuestionState {
    employee: Employee;
    question: QuestionState;
    answer: string | any;
}

@Component({
    selector: 'app-review-results',
    templateUrl: './review-results.template.html',
    styleUrls: ['./review-results.style.scss'],
})
export class ReviewResultsComponent implements OnInit {
    isActive = false;

    @Input() review: any = {};
    @Input() performanceReviewImprovements: boolean = false;
    @Input() canExportResults: boolean = false;

    fields: any[] = [];
    fieldsCount = 0;
    assignments: ReviewAssignment[] = [];

    answeredQuestionsCollection: AnsweredQuestionState[][] = [];
    lastQuestions: string[] = [];

    tab = 'fields';

    goals: Goal[] = [];
    feedbackReceived: Feedback[] = [];
    feedbackGiven: Feedback[] = [];

    protected readonly feedbackTypes = FeedbackTypesDisplayOptions;

    constructor(
        private element: ElementRef,
        private readonly reportQuery: Report,
        private readonly notifyService: NotifyService
    ) {}

    ngOnInit(): void {
        this.renderElements();
    }

    print(): void {
        const printContent = $('.form.fields.printable')[0];

        const $clonedContent = $(printContent).clone();

        const prtContentStyles = $('style');
        const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');
        WinPrint.document.write('<html><head></head><body></body></html>');
        WinPrint.document.getElementsByTagName('head')[0].innerHTML +=
            '<link rel="stylesheet" type= "text/css" href= "/assets/semantic/semantic.min.css">';
        WinPrint.document.getElementsByTagName('head')[0].innerHTML +=
            '<link href="https://use.fontawesome.com/1c93c020aa.css" media="all" rel="stylesheet">';
        WinPrint.document.getElementsByTagName('body')[0].innerHTML += $clonedContent[0].innerHTML;
        prtContentStyles.each(function (_i: number, o: any): any {
            WinPrint.document.getElementsByTagName('body')[0].innerHTML += '<style>' + $(o).html() + '</style>';
        });
        WinPrint.document.getElementsByTagName('body')[0].innerHTML +=
            '<style> body { zoom: .8; font - size: 12px; -moz - transform: scale(0.8); -moz - transform - origin: 0 0; }</style>';

        WinPrint.document.close();
        WinPrint.focus();
        setTimeout(() => {
            WinPrint.print();
            WinPrint.close();
        }, 1500);
    }

    onExport(contentType: string): void {
        this.reportQuery
            .name('reviewResultReport')
            .filters([{ name: 'review_id', value: this.review.id }])
            .get()
            .then((report: Report) => {
                report.export(contentType);
            })
            .catch(() => {
                this.notifyService.error('performance.errorExportingReport');
                this.exit();
            });
    }

    changeTab(tab: string): void {
        this.tab = tab;
        this.renderProgress();
        this.renderElements();
    }

    launch(onlyForEmployee: Employee = null): void {
        this.loadFields(onlyForEmployee);
        this.loadPerformanceData();

        const $container = $('.review-results-container');
        $container.width($(window).width()).height($(window).height());
        $container.fadeIn(() => {
            this.isActive = true;
            this.renderElements();
        });

        $(window).resize(() => {
            $container.width($(window).width()).height($(window).height());
        });
    }

    exit(): void {
        $('.review-results-container').fadeOut(() => {
            this.isActive = false;
        });
    }

    trackByFn(index: number, item: any): any {
        return item ? item.index : index;
    }

    getGoalsBadgeType(): BadgeType {
        const numCompleteGoals = this.goals.filter((goal) => goal.isComplete).length;
        if (numCompleteGoals === 0) {
            return 'danger';
        } else if (numCompleteGoals === this.goals.length) {
            return 'success';
        }
        return 'warning';
    }

    getFeedbackBadgeType(feedbackLength: number): BadgeType {
        if (feedbackLength === 0) {
            return 'danger';
        }
        return 'success';
    }

    private renderProgress(): void {
        setTimeout(() => {
            $(this.element.nativeElement).find('.ui.progress').progress();
        });
    }

    private renderRating(): void {
        setTimeout(() => {
            $(this.element.nativeElement).find('.ui.rating').rating({ interactive: false });
        });
    }

    private renderElements(): void {
        this.renderRating();
    }

    private loadFields(onlyForEmployee: Employee = null): void {
        this.assignments = [...this.review.assignments].filter((assignment) => assignment.response?.jsonData);
        // Use the first assignments copy of the template as the set of questions
        // if the template changes between assignments this does not handle it
        this.fields = this.review.assignments.find((assignment) => assignment.jsonData).jsonData;
        const lengths = this.review.assignments.map((assignment) => assignment.jsonData?.length);
        this.fieldsCount = Math.max(...lengths);

        /**
         * Build show-able review results by merging all responses, grouped by question positions.
         * This means if a template changes, question copy can be different within each "Question".
         */
        this.answeredQuestionsCollection = new Array(this.fieldsCount);
        this.answeredQuestionsCollection = this.answeredQuestionsCollection.fill(null).map(() => []);

        this.lastQuestions = [];

        this.review.assignments.forEach(function (assignment: ReviewAssignment): void {
            if (onlyForEmployee && onlyForEmployee.isNot(assignment.employee)) {
                return;
            }

            for (let questionIndex = 0; questionIndex < this.fieldsCount; questionIndex++) {
                const question: QuestionState = assignment.jsonData[questionIndex];

                /**
                 * To improve UI a little, remove duplicate question text thats same as last one - means we dont show same question
                 * over and over. This means that the UI should look totally normal unless someone changes a template and assigns
                 * the review to someone.
                 */
                const questionSeen = !(typeof this.lastQuestions[questionIndex] === 'undefined');
                const questionIsSame = questionSeen && this.lastQuestions[questionIndex] === question?.question;
                if (questionIsSame) {
                    question.question = null;
                }

                let answer: string | any = null;

                if (assignment.response?.jsonData) {
                    answer = assignment.response.jsonData['question' + questionIndex] ?? null;
                }

                this.answeredQuestionsCollection[questionIndex].push({
                    employee: assignment.employee,
                    question: question,
                    answer: answer,
                });
                this.lastQuestions[questionIndex] = assignment.jsonData[questionIndex]?.question;
            }
        }, this);

        this.renderElements();
    }

    private loadPerformanceData(): void {
        Goal.where('employeeId', this.review.subjectId)
            .where('periodStart', this.review.periodStart)
            .where('periodEnd', this.review.periodEnd)
            .with('creator')
            .all()
            .then(([goals, _]) => {
                this.goals = goals;
            });
        Feedback.where('subjectId', this.review.subjectId)
            .where('periodStart', this.review.periodStart)
            .where('periodEnd', this.review.periodEnd)
            .with(['creator', 'subject'])
            .all()
            .then(([feedbacks, _]) => {
                this.feedbackReceived = feedbacks;
            });
        Feedback.where('createdBy', this.review.subjectId)
            .where('periodStart', this.review.periodStart)
            .where('periodEnd', this.review.periodEnd)
            .with(['creator', 'subject'])
            .all()
            .then(([feedbacks, _]) => {
                this.feedbackGiven = feedbacks;
            });
    }
}
